import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { MaestroProvider, MaestroDebugContext } from '@ae/maestrov2-form-lib';
import CopilotLayout from './CopilotLayout';
import ProgramRendererUrlSync from '../ProgramRendererUrlSync';
import { DeveloperDock } from './DeveloperPanel';
import DragBar from './DragBar';
import { usePointerDrag } from './usePointerDrag';
import { buildCopilotTheme } from './copilotTheme';
import { getDrafts, subscribeDrafts, resolveProfile } from './copilotProfileDraft';
import { canvasViewport, canvasRect } from './copilotCanvas';

// The gap the window keeps from the top of the screen, and the height of the
// bar that must never leave it.
const MARGIN = 32;
const BAR_HEIGHT = 26;
const WINDOW_WIDTH = 700;
const DRAG_LABEL = 'Move the caption window';

/**
 * The Copilot caption window.
 *
 * `<Maestro>` is MaestroProvider plus a fixed chrome; this keeps the Provider —
 * and therefore the whole engine: caption resolution, binding evaluation, the
 * dispatch chain, BAKE, exit actions, broker send and the numpad keys — and
 * supplies Copilot's own arrangement in place of that chrome. Every Maestro
 * prop is forwarded untouched, so what reaches the broker is unchanged.
 *
 * `profile` decides which fragments the arrangement renders. It is ours, not
 * the library's, so it is destructured out rather than spread onto the
 * Provider.
 *
 * The window is draggable by the same bar the developer dock wears, and the
 * offset that leaves behind persists in uiSettings.
 *
 * The Dialog sits INSIDE the Provider so closing the window does not tear down
 * resolved caption state — the same reason the standard chrome keeps itself
 * mounted. `onClose` is both used here and forwarded, mirroring ComposeMaestro.
 *
 * The developer dock is a sibling of the Dialog for the same reason one step
 * further out: a closed Dialog unmounts its children, and the preview renderer
 * inside the dock is expensive to load. Whether it goes with the window is the
 * `previewLatched` switch.
 *
 * Whether the developer strip shows is passed IN, not held here: the host
 * keys this component on the graphic id, so owning that state would reset it
 * every time the operator opened a different graphic — and the preview the
 * switches turn on would vanish with it.
 */
/**
 * The window itself: the Dialog, the bar it is dragged by, and the offset that
 * drag leaves behind.
 *
 * MUI centres the Paper, so moving it is a translate away from that centre
 * rather than a position — which also means it re-centres correctly on a
 * resize and needs nothing measured before the first paint. Inside the
 * Provider, so the offset persists through the same uiSettings the preview
 * persists its width through.
 */
function CopilotWindow({ open, onClose, theme, children }) {
  const debug = useContext(MaestroDebugContext);
  const setUiSettings = debug?.setUiSettings;
  const saved = debug?.uiSettings?.copilotWindowOffset || null;

  const [offset, setOffset] = useState(saved);
  const origin = useRef(null);
  const latest = useRef(saved);

  /*
   * The drag may not carry the title bar off the screen: it is the only way to
   * bring the window back, and an operator cannot scroll a fixed dialog.
   *
   * Anchoring the dialog to the top makes its resting position a constant —
   * MARGIN down, centred across — so the clamp needs nothing measured, and
   * works before the first paint and while the dialog is mid-transition.
   */
  const clamp = useCallback((next) => {
    const { width: screenWidth, height: screenHeight } = canvasViewport();
    const width = Math.min(WINDOW_WIDTH, screenWidth);
    const restingLeft = Math.max(0, (screenWidth - width) / 2);

    const between = (value, low, high) => Math.min(Math.max(value, low), high);

    /*
     * The height is the one thing that cannot be known in advance — a caption
     * offering nineteen types is taller than one offering none — so it is read
     * off the window, which the bar's own label finds unambiguously. Without
     * it, the fallback keeps the bar on screen and lets the body run past the
     * bottom edge.
     */
    const paper = document.querySelector(`[aria-label="${DRAG_LABEL}"]`)?.parentElement;
    const height = canvasRect(paper?.getBoundingClientRect())?.height || 0;
    const lowestTop = height
      ? Math.max(0, screenHeight - height)
      : screenHeight - BAR_HEIGHT - MARGIN;

    // Wholly on screen, the same rule the developer dock follows.
    return {
      x: between(next.x, -restingLeft, screenWidth - width - restingLeft),
      y: between(next.y, -MARGIN, Math.max(-MARGIN, lowestTop - MARGIN)),
    };
  }, []);

  /*
   * The same clamp on open and on resize. A position remembered from a taller
   * screen would otherwise put the bar out of reach the moment the window
   * came back.
   */
  useEffect(() => {
    if (!open) return undefined;

    const pullIntoView = () => setOffset((current) => {
      if (!current) return current;
      const next = clamp(current);
      latest.current = next;
      return next;
    });

    // After a paint: the dialog has no laid-out height while this effect runs,
    // and without the height the clamp falls back to keeping only the bar on
    // screen, which is how a tall window kept opening past the bottom edge.
    const frame = requestAnimationFrame(pullIntoView);
    window.addEventListener('resize', pullIntoView);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', pullIntoView);
    };
  }, [clamp, open]);

  const { dragging, startDrag } = usePointerDrag({
    onStart: () => { origin.current = latest.current || { x: 0, y: 0 }; },
    onMove: (delta) => {
      const next = clamp({ x: origin.current.x + delta.x, y: origin.current.y + delta.y });
      latest.current = next;
      setOffset(next);
    },
    onEnd: () => setUiSettings?.((previous) => ({ ...previous, copilotWindowOffset: latest.current })),
  });

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      // The developer dock sits outside the window; without this the dialog
      // pulls focus back and nothing can be typed into the dock.
      disableEnforceFocus
      maxWidth={false}
      /*
        Anchored to the top rather than centred. A centred dialog grows in both
        directions, so a tall caption — gfx_PermScore offering all nineteen of
        its types — pushed the title bar above the viewport, where nothing can
        reach it. Anchored, the window only ever grows downwards.
      */
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      slotProps={{
        paper: {
          sx: {
            width: 700,
            maxWidth: '100%',
            mt: `${MARGIN}px`,
            // And it stops at the bottom edge rather than running past it.
            maxHeight: `calc(100vh - ${MARGIN * 2}px)`,
            bgcolor: theme.palette.background.paper,
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 2,
            backgroundImage: 'none',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transform: offset ? `translate(${offset.x}px, ${offset.y}px)` : 'none',
          },
        },
      }}
    >
      <ThemeProvider theme={theme}>
        {/* The same bar the developer dock and the drive chrome are moved by. */}
        <DragBar label={DRAG_LABEL} onPointerDown={startDrag} dragging={dragging} />
        {/* The bar stays put; a window taller than the screen scrolls beneath it. */}
        <Box sx={{ minHeight: 0, overflowY: 'auto' }}>{children}</Box>
      </ThemeProvider>
    </Dialog>
  );
}

export default function CopilotMaestroChrome({
  profile,
  developerOpen = false,
  graphicSubtitle,
  ...maestroProps
}) {
  /*
   * Runtime fragment choices from the developer strip, layered over the
   * committed profile. They are a developer's working copy on the way to
   * copilotChromeProfiles.js, so an explicitly passed profile still wins.
   */
  const drafts = useSyncExternalStore(subscribeDrafts, getDrafts);
  const resolved = useMemo(
    () => profile || resolveProfile(drafts, maestroProps.graphicId),
    [profile, drafts, maestroProps.graphicId],
  );
  const { open, onClose } = maestroProps;
  const hostTheme = useTheme();
  const theme = useMemo(() => buildCopilotTheme(hostTheme), [hostTheme]);

  return (
    <MaestroProvider {...maestroProps}>
      <ProgramRendererUrlSync rendererId={maestroProps.rendererId} />
      <CopilotWindow open={open} onClose={onClose} theme={theme}>
        <CopilotLayout
          profile={resolved}
          graphicName={maestroProps.graphicName}
          graphicSubtitle={graphicSubtitle}
          graphicId={maestroProps.graphicId}
          showDeveloperStrip={developerOpen}
        />
      </CopilotWindow>

      {/*
        Beside the Dialog, not inside it: MUI unmounts a closed Dialog's
        children, which would take the preview renderer down and reload it on
        every reopen. Composing our own chrome means nothing forces it inside.
      */}
      <DeveloperDock windowOpen={Boolean(open)} />
    </MaestroProvider>
  );
}
